import {
  Box,
  Typography,
  Card,
  Button,
  Stack,
  CircularProgress,
  Grid,
  Modal,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  CheckCircleOutline,
  CancelOutlined,
  Download,
  Close,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/webpack";
import {
  getOfferLetter,
  respondTheoffer,
} from "../../../../Redux/Reducers/JobReducer";
import { useQuantityContext } from "../../../QuantityContext";

const REASONS = [
  "Got a better offer",
  "Not the right role",
  "Company culture mismatch",
  "Salary expectations not met",
  "Relocation issues",
  "Decided to pursue higher studies",
];

const ApplicantOfferLetter = () => {
  const dispatch = useDispatch();
  const offerLetters = useSelector((state) => state?.jobs?.OfferLetters) || [];
  const offer = offerLetters[0];
  const [isLoading, setIsLoading] = useState(true);
  const [imageData, setImageData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [acceptDialog, setAcceptDialog] = useState(false);
  const [declineDialog, setDeclineDialog] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [declineRemarks, setDeclineRemarks] = useState("");
  const [acceptRemarks, setAcceptRemarks] = useState("");

  const { handleFailure, handleInfo } = useQuantityContext();
  const convertPdfToImage = async (url) => {
    try {
      setIsLoading(true);
      const loadingTask = pdfjsLib.getDocument(url);
      const pdf = await loadingTask.promise;
      const numPages = pdf.numPages;
      const imageUrls = [];

      for (let pageNumber = 1; pageNumber <= numPages; pageNumber++) {
        const page = await pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 2.0 });
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context, viewport }).promise;
        imageUrls.push(canvas.toDataURL("image/png"));
      }

      setImageData(imageUrls);
    } catch (error) {
      console.error("Error converting PDF to image:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (offer?.offer_letter_file) {
      convertPdfToImage(offer?.offer_letter_file);
    }
  }, [offer]);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = offer?.offer_letter_file;
    link.download = `OfferLetter_${offer.designation.replace(/\s+/g, "_")}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleModalOpen = () => {
    setModalLoading(true);
    setOpenModal(true);
    setTimeout(() => setModalLoading(false), 500);
  };

  const handleAcceptOffer = async () => {
    try {
      setIsSubmitting(true);
      const payload = {
        application_id: selectedOffer?.application_id,
        action: "ACCEPT",
        applicant_remarks: acceptRemarks || "Excited to join the team!",
      };
      await dispatch(respondTheoffer(payload)).unwrap();
      await dispatch(getOfferLetter(selectedOffer?.application_id));
      setAcceptDialog(false);
    } catch (error) {
      console.error("Error accepting offer:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeclineOffer = async () => {
    try {
      setIsSubmitting(true);
      if (!declineReason) {
        handleInfo("Please select a reason for declining");
        return;
      }
      const payload = {
        application_id: selectedOffer?.application_id,
        action: "DECLINE",
        rejection_reason: declineReason,
        applicant_remarks: declineRemarks,
      };
      await dispatch(respondTheoffer(payload)).unwrap();
      await dispatch(getOfferLetter(selectedOffer?.application_id));
      setDeclineDialog(false);
    } catch (error) {
      console.error("Error declining offer:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    height: "90%",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 2,
    borderRadius: 2,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  if (!offerLetters?.length)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <Typography variant="h6">No offer letter found</Typography>
      </Box>
    );

  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
      <Card sx={{ width: "100%", maxWidth: 800, backgroundColor: "#f8f9fa" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          {offer?.status === "Offer Released" && (
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setAcceptDialog(true);
                  setSelectedOffer(offer);
                }}
                disabled={isSubmitting}
              >
                Accept Offer
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setDeclineDialog(true);
                  setSelectedOffer(offer);
                }}
                disabled={isSubmitting}
              >
                Decline Offer
              </Button>
            </Box>
          )}
        </Box>

        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              height: 400,
              boxShadow: 2,
            }}
          >
            <CircularProgress />
          </Box>
        ) : imageData ? (
          <Grid container spacing={2} sx={{ mb: 4, mt: 3 }}>
            {imageData?.map((img, index) => (
              <Grid item xs={12} key={index}>
                <Card
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    boxShadow: 2,
                    cursor: "pointer",
                  }}
                  onClick={handleModalOpen}
                >
                  <img
                    src={img}
                    alt={`Offer letter page ${index + 1}`}
                    style={{ width: "100%", height: "auto", borderRadius: 8 }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography color="error" sx={{ p: 2, textAlign: "center" }}>
            Failed to load offer letter
          </Typography>
        )}

        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box sx={modalStyle}>
            <Stack
              direction="row"
              justifyContent="right"
              spacing={1}
              sx={{
                mb: 2,
                position: "sticky",
                top: 0,
                bgcolor: "background.paper",
                zIndex: 1,
                // boxShadow: 2,
                p: 1,
              }}
            >
              
              <IconButton onClick={() => setOpenModal(false)}>
                <Close />
              </IconButton>
            </Stack>

            {modalLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
               <Box sx={{ display: "flex", justifyContent: "right", width: "100%", mb: 2 }}>
                 <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleDownload}
                sx={{ textTransform: "none" }}
              >
                Download
              </Button>
               </Box>
                {imageData?.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`Offer letter page ${index + 1}`}
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      marginBottom: 16,
                      objectFit: "contain",
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Modal>

        {/* Accept Offer Dialog */}
        <Dialog
          open={acceptDialog}
          onClose={() => {
            setAcceptDialog(false);
            setAcceptRemarks("");
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Accept Offer</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Would you like to add any remarks while accepting the offer?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={acceptRemarks}
              onChange={(e) => setAcceptRemarks(e.target.value)}
              placeholder="Enter your remarks (optional)"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setAcceptDialog(false);
                setAcceptRemarks("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAcceptOffer}
              color="primary"
              variant="contained"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Accepting..." : "Accept Offer"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Decline Offer Dialog */}
        <Dialog
          open={declineDialog}
          onClose={() => {
            setDeclineDialog(false);
            setDeclineReason("");
            setDeclineRemarks("");
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Decline Offer</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Please select a reason for declining:
            </Typography>
            <TextField
              select
              fullWidth
              required
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              error={!declineReason}
              helperText={!declineReason ? "Reason is required" : ""}
              sx={{ mb: 3 }}
            >
              {REASONS.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </TextField>

            <Typography sx={{ mb: 1 }}>
              Additional Remarks (Optional):
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={declineRemarks}
              onChange={(e) => setDeclineRemarks(e.target.value)}
              placeholder="Enter any additional comments..."
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDeclineDialog(false);
                setDeclineReason("");
                setDeclineRemarks("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeclineOffer}
              color="error"
              variant="contained"
              disabled={isSubmitting || !declineReason}
            >
              {isSubmitting ? "Declining..." : "Decline Offer"}
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Box>
  );
};

export default ApplicantOfferLetter;
